import React, {createContext, ReactNode, useContext, useEffect, useRef, useState} from 'react';
import {CurrentUser} from "../types/auth";
import {useLocalStorage} from "../controllers/localStorage-controller";
import {useLocation} from "react-router-dom";
import {openWarningNotification} from "../helpers/mainHelpers";
import {Account} from "appwrite";
import {client} from "../lib/appwrite";
import {canManageSiteContent} from "../helpers/canManageSiteContent";


interface UserContextType {
  currentUser: CurrentUser | null;
  isLoggedIn: boolean;
  canManageContent: boolean;
  isAdmin: boolean;
  toggleAuthFlag: () => void,
}

interface UserProviderProps {
  children: ReactNode;
}

const account = new Account(client);

const UserContext = createContext<UserContextType | undefined>(undefined);

export const AuthProvider: React.FC<UserProviderProps> = ({children}) => {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [lastUpdate, setLastUpdate] = useState(0)
  const [verifiedUser, setVerifiedUser] = useState<CurrentUser | null>(null)
  const {getAuthUserData, clearAuthUserData} = useLocalStorage()
  const location = useLocation();
  const lastCheckedRef = useRef(0);
  const CHECK_INTERVAL = 60 * 1000; // 60 секунд


  useEffect(() => {
    let active = true;
    const checkSession = async () => {
      if (!isLoggedIn) {
        setVerifiedUser(null);
        return;
      }

      const now = Date.now();
      if (verifiedUser && now - lastCheckedRef.current < CHECK_INTERVAL) return;

      lastCheckedRef.current = now;
      setVerifiedUser(null);

      try {
        const user = await account.get();
        if (!active) return;
        const authUser = {id: user.$id, name: user.name, email: user.email, labels: user.labels ?? []};
        setVerifiedUser(authUser);
        setCurrentUser(authUser);
      } catch (err) {
        if (!active) return;
        openWarningNotification('Сесія недійсна: потрібно авторизуватися повторно!')
        clearAuthUserData()
        setCurrentUser(null)
        setVerifiedUser(null)
        setIsLoggedIn(false)
      }
    };

    checkSession();
    return () => { active = false; };
  }, [location.pathname, isLoggedIn, lastUpdate]);


  useEffect(() => {
    const savedUser = getAuthUserData();
    if (savedUser) {
      setCurrentUser(savedUser);
      setIsLoggedIn(true);
    } else {
      setCurrentUser(null)
      setIsLoggedIn(false)
    }
  }, [lastUpdate]);

  const toggleAuthFlag = () => {
    setLastUpdate(Date.now())
  }


  return (
    <UserContext.Provider value={{currentUser, isLoggedIn, isAdmin: isLoggedIn && !!verifiedUser?.labels.includes('admin'), canManageContent: isLoggedIn && canManageSiteContent(verifiedUser), toggleAuthFlag}}>
      {children}
    </UserContext.Provider>
  );
};

export const useAuth = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
